import { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Copy, X, Plus, Clock } from "lucide-react";
import { Toast } from "../../components/Toast";

type TimeZone = {
  id: string;
  name: string;
};

const POPULAR_ZONES = [
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
  "America/Chicago",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Bucharest",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Dubai",
  "Australia/Sydney",
];

const getAllTimezones = (): string[] => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    // Fallback for older browsers
    return POPULAR_ZONES;
  }
};

const formatTimezoneLabel = (tz: string): string => {
  return tz.replace(/_/g, " ").replace(/\//g, " / ");
};

const getTimezoneOffset = (date: Date, timezone: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    });
    const parts = formatter.formatToParts(date);
    const offsetPart = parts.find((p) => p.type === "timeZoneName");
    return offsetPart?.value || "";
  } catch {
    return "";
  }
};

const formatTimeInZone = (
  date: Date,
  timezone: string,
): { date: string; time: string; offset: string } => {
  try {
    const dateFormatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    const timeFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return {
      date: dateFormatter.format(date),
      time: timeFormatter.format(date),
      offset: getTimezoneOffset(date, timezone),
    };
  } catch {
    return { date: "", time: "", offset: "" };
  }
};

const STORAGE_KEY = "peslar-timezone-selected";

const loadSavedZones = (): string[] => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch {
    // Ignore errors
  }
  return ["UTC", "America/New_York", "Europe/London"];
};

const saveZones = (zones: string[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(zones));
  } catch {
    // Ignore errors
  }
};

export function TimeZoneConverter() {
  const { t } = useTranslation();
  const [sourceDate, setSourceDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split("T")[0];
  });
  const [sourceTime, setSourceTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  });
  const [selectedZones, setSelectedZones] = useState<string[]>(loadSavedZones);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const allTimezones = useMemo(() => getAllTimezones(), []);

  useEffect(() => {
    saveZones(selectedZones);
  }, [selectedZones]);

  const sourceDateTime = useMemo(() => {
    const [year, month, day] = sourceDate.split("-").map(Number);
    const [hours, minutes] = sourceTime.split(":").map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  }, [sourceDate, sourceTime]);

  const filteredZones = useMemo(() => {
    const query = searchQuery.toLowerCase();
    const available = allTimezones.filter((tz) => !selectedZones.includes(tz));

    if (!query) {
      // Show popular zones first when no search
      const popular = POPULAR_ZONES.filter((tz) => !selectedZones.includes(tz));
      const others = available.filter((tz) => !POPULAR_ZONES.includes(tz));
      return [...popular, ...others].slice(0, 20);
    }

    return available
      .filter((tz) => tz.toLowerCase().includes(query))
      .slice(0, 20);
  }, [allTimezones, selectedZones, searchQuery]);

  const useCurrentTime = useCallback(() => {
    const now = new Date();
    setSourceDate(now.toISOString().split("T")[0]);
    setSourceTime(
      `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
    );
  }, []);

  const addTimezone = useCallback((tz: string) => {
    setSelectedZones((prev) => [...prev, tz]);
    setShowDropdown(false);
    setSearchQuery("");
  }, []);

  const removeTimezone = useCallback((tz: string) => {
    setSelectedZones((prev) => prev.filter((z) => z !== tz));
  }, []);

  const copyTime = useCallback(
    async (tz: string) => {
      const formatted = formatTimeInZone(sourceDateTime, tz);
      const text = `${formatted.date} ${formatted.time} ${formatted.offset}`.trim();
      try {
        await navigator.clipboard.writeText(text);
        setToastMessage(t("timezoneConverter.copied"));
      } catch {
        setToastMessage(t("timezoneConverter.copyFailed"));
      }
    },
    [sourceDateTime, t],
  );

  return (
    <div className="timezone-converter">
      <div className="tz-source-section">
        <div className="tz-source-card">
          <label className="tz-label">{t("timezoneConverter.sourceTime")}</label>
          <div className="tz-source-inputs">
            <input
              type="date"
              className="tz-input"
              value={sourceDate}
              onChange={(e) => setSourceDate(e.target.value)}
            />
            <input
              type="time"
              className="tz-input"
              value={sourceTime}
              onChange={(e) => setSourceTime(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="tz-current-btn"
            onClick={useCurrentTime}
          >
            <Clock size={16} />
            {t("timezoneConverter.useCurrentTime")}
          </button>
        </div>
      </div>

      <div className="tz-add-section">
        <div className="tz-dropdown-container">
          <button
            type="button"
            className="tz-add-btn"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <Plus size={16} />
            {t("timezoneConverter.addTimezone")}
          </button>
          {showDropdown && (
            <div className="tz-dropdown">
              <input
                type="text"
                className="tz-search-input"
                placeholder={t("timezoneConverter.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <div className="tz-dropdown-list">
                {!searchQuery && filteredZones.some((tz) => POPULAR_ZONES.includes(tz)) && (
                  <div className="tz-dropdown-label">
                    {t("timezoneConverter.popularZones")}
                  </div>
                )}
                {filteredZones.map((tz) => (
                  <button
                    key={tz}
                    type="button"
                    className="tz-dropdown-item"
                    onClick={() => addTimezone(tz)}
                  >
                    {formatTimezoneLabel(tz)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedZones.length === 0 ? (
        <div className="tz-empty">{t("timezoneConverter.noZones")}</div>
      ) : (
        <div className="tz-zones-list">
          {selectedZones.map((tz) => {
            const formatted = formatTimeInZone(sourceDateTime, tz);
            return (
              <div key={tz} className="tz-zone-card">
                <div className="tz-zone-header">
                  <span className="tz-zone-name">{formatTimezoneLabel(tz)}</span>
                  <div className="tz-zone-actions">
                    <button
                      type="button"
                      className="tz-icon-btn"
                      onClick={() => copyTime(tz)}
                      aria-label={t("timezoneConverter.copy")}
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      type="button"
                      className="tz-icon-btn tz-remove-btn"
                      onClick={() => removeTimezone(tz)}
                      aria-label={t("timezoneConverter.remove")}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
                <div className="tz-zone-time">
                  <span className="tz-time-value">
                    {formatted.date} {formatted.time}
                  </span>
                  <span className="tz-time-offset">{formatted.offset}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
